import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const LOGO_DARK = "/logo-footer.png";

const CATALOG_LINKS = ["Primi Piatti Pronti", "Secondi Piatti Pronti", "Contorni", "Fritti", "Kris", "Dolci", "Sughi Pronti", "Gluten Fit"];

const INFO_LINKS = [
  { to: "/packaging", label: "Packaging sostenibile", testid: "footer-packaging-link" },
  { to: "/certificazioni", label: "Certificazioni & Qualità", testid: "footer-certificazioni-link" },
  { to: "/ordine", label: "Traccia il tuo ordine", testid: "footer-track-link" },
  { to: "/b2b", label: "Rivenditori B2B", testid: "footer-b2b-link" },
  { to: "/admin", label: "Area admin", testid: "footer-admin-link" },
];

const NewsletterBanner = () => {
  const [email, setEmail] = useState("");
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/newsletter/subscribe", { email });
      setSub(r.data);
      toast.success(r.data.already_subscribed ? "Sei già iscritto! Ecco il tuo codice" : "Iscrizione completata! Ti abbiamo inviato il codice via email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore, riprova");
    }
    setLoading(false);
  };

  return (
    <div className="border-b border-[#33463A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#7DC242] mb-3 flex items-center gap-2">
            <Gift className="w-4 h-4" /> Newsletter
          </p>
          <h3 className="font-serif-display font-bold text-2xl sm:text-3xl">Iscriviti e ricevi il 10% di sconto</h3>
          <p className="mt-2 text-sm text-[#FDFBF7]/60 max-w-md">Lascia la tua email: ti inviamo subito un codice sconto del 10% da usare al checkout sul tuo prossimo ordine.</p>
        </div>
        {sub ? (
          <div data-testid="newsletter-success" className="rounded-2xl bg-[#FDFBF7]/5 border border-[#7DC242]/40 p-6 text-center">
            <p className="text-sm text-[#FDFBF7]/70">Il tuo codice sconto del {sub.discount_percent}%:</p>
            <p data-testid="newsletter-code" className="font-mono-accent font-bold text-2xl text-[#7DC242] mt-2 tracking-widest">{sub.code}</p>
            <p className="text-xs text-[#FDFBF7]/50 mt-2">{sub.used ? "Questo codice è già stato utilizzato" : "Inseriscilo al checkout · valido una volta"}</p>
          </div>
        ) : (
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="La tua email"
              className="flex-1 rounded-full bg-[#FDFBF7]/10 border border-[#FDFBF7]/20 px-6 py-4 text-sm placeholder:text-[#FDFBF7]/40 focus:outline-none focus:border-[#7DC242] transition-colors"
            />
            <button
              data-testid="newsletter-submit-btn"
              type="submit"
              disabled={loading}
              className="px-8 py-4 rounded-full font-semibold text-sm bg-[#7DC242] text-[#243329] hover:bg-[#6BAB35] transition-colors disabled:opacity-50"
            >
              {loading ? "Invio..." : "Ottieni il -10%"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const FooterNav = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
    <div>
      <img src={LOGO_DARK} alt="Piatti Pronti Gluten Free" className="h-9 w-auto mb-5 -ml-1" />
      <p className="text-sm text-[#FDFBF7]/60 leading-relaxed max-w-xs">
        La cucina italiana senza glutine. Piatti pronti genuini, 100% italiani, pronti in 5 minuti, consegnati in tutta Italia.
      </p>
    </div>
    <div>
      <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#7DC242] mb-4">Catalogo</p>
      <ul className="space-y-2 text-sm text-[#FDFBF7]/70">
        {CATALOG_LINKS.map((cat) => (
          <li key={cat}><Link className="hover:text-[#FDFBF7] transition-colors" to={`/prodotti?cat=${cat}`}>{cat}</Link></li>
        ))}
      </ul>
    </div>
    <div>
      <p className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#7DC242] mb-4">Info</p>
      <ul className="space-y-2 text-sm text-[#FDFBF7]/70">
        <li>Ordine minimo 100€</li>
        <li>Spedizione refrigerata in tutta Italia</li>
        <li>Prodotti certificati gluten free</li>
        {INFO_LINKS.map((l) => (
          <li key={l.to}><Link className="hover:text-[#FDFBF7] transition-colors" to={l.to} data-testid={l.testid}>{l.label}</Link></li>
        ))}
      </ul>
    </div>
  </div>
);

export const Footer = () => (
  <footer className="bg-[#243329] text-[#FDFBF7] mt-0">
    <NewsletterBanner />
    <FooterNav />
    <div className="border-t border-[#33463A] py-6 text-center font-mono-accent text-xs text-[#FDFBF7]/40 tracking-widest">
      © 2026 PIATTI PRONTI GLUTEN FREE — CHEF MICHEL
    </div>
  </footer>
);
